import { Alert, AlertType } from 'components/Alert/Alert'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { DisplayCard } from 'components/DisplayCards/DisplayCard'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Badge } from 'components/Badge/Badge'
import { Box, Flex, ColorVariant, colors } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import { IKitchenDashboardTabProps } from 'types/kitchen/kitchenTabProps.interface'
import { IKitchenIngredient } from 'types/kitchen/kitchenIngredient.interface'
import { IKitchenSite } from 'types/kitchen/kitchenSite.interface'
import { KitchenBatchTag } from '../Kitchen.styled'

export const DashboardTab = ({ ingredients, dishes, kitchens, costPerPortion }: IKitchenDashboardTabProps) => {
  const expiring = ingredients.flatMap((i) =>
    i.batches
      .filter((b) => b.soon || b.critical)
      .map((b) => ({ ...b, name: i.name, unit: i.unit })),
  )

  const storeColumns: IHonestTableColumn<IKitchenIngredient>[] = [
    {
      id: 'name',
      header: 'Ingredient',
      sticky: true,
      minWidth: '10rem',
      cell: (i) => (
        <Cell>
          <BodyText weight='600'>{i.name}</BodyText>
        </Cell>
      ),
    },
    {
      id: 'stock',
      header: 'Stock',
      cell: (i) => (
        <BodyText>
          {i.stock} {i.unit}
        </BodyText>
      ),
    },
    {
      id: 'batches',
      header: 'Batches (FEFO order)',
      cell: (i) => (
        <Flex wrap gap='0.25rem'>
          {i.batches.map((b, j) => (
            <KitchenBatchTag key={j} $critical={b.critical} $soon={b.soon}>
              {b.qty}
              {i.unit} · exp {b.exp}
            </KitchenBatchTag>
          ))}
        </Flex>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (i) => {
        const critical = i.batches.some((b) => b.critical)
        const soon = i.batches.some((b) => b.soon)
        const label = critical ? 'Use first' : soon ? 'Expiring soon' : 'OK'
        return (
          <Badge text={label} red={critical} orange={soon} green={!critical && !soon} />
        )
      },
    },
  ]

  return (
    <Flex column gap='1rem' width='100%'>
      {expiring.length > 0 && (
        <Alert
          title='Expiry pressure — Main Kitchen store'
          description={`${expiring.map((b) => `${b.qty}${b.unit} ${b.name} (exp ${b.exp})`).join(' · ')}. See Suggestions for dishes that consume these first.`}
          type={AlertType.WARNING}
          colorVariant={ColorVariant.PRIMARY}
        />
      )}
      <Flex gap='0.875rem' width='100%' wrap>
        {kitchens.map((k) => (
          <KitchenCard key={k.id} kitchen={k} dishes={dishes} costPerPortion={costPerPortion} />
        ))}
      </Flex>
      <Container>
        <ContainerHeader>
          <TitleText weight='700'>Kitchen Store Health — Main Kitchen (from central Inventory ledger)</TitleText>
        </ContainerHeader>
        <ContainerContent>
          <HonestTable columns={storeColumns} data={ingredients} keyField='id' />
        </ContainerContent>
      </Container>
    </Flex>
  )
}

const KitchenCard = ({
  kitchen,
  dishes,
  costPerPortion,
}: {
  kitchen: IKitchenSite
  dishes: IKitchenDashboardTabProps['dishes']
  costPerPortion: (dish: IKitchenDashboardTabProps['dishes'][0]) => number
}) => {
  const stats =
    kitchen.id === 1
      ? [
          ['Lunch ordered', 120],
          ['Dinner planned', 108],
          ['Variance', `${kitchen.variancePct}%`],
        ]
      : [
          ['Lunch ordered', 58],
          ['Dinner planned', 54],
          ['Open orders', 2],
        ]

  return (
    <Box width='100%'>
      <Flex between center mb='12'>
        <BodyText weight='700'>{kitchen.name}</BodyText>
        <Badge text={kitchen.supplyMode === 'CLIENT_SUPPLIED' ? 'Client-supplied' : `Contractor — ${kitchen.contractor}`} green={kitchen.supplyMode === 'CLIENT_SUPPLIED'} blue={kitchen.supplyMode !== 'CLIENT_SUPPLIED'} />
      </Flex>
      <Flex gap='0.5rem' mb='12' width='100%'>
        {stats.map(([l, v]) => (
          <DisplayCard key={l as string} value={v as string | number} label={l as string} borderColor={colors.orange} />
        ))}
      </Flex>
      {kitchen.id === 1 ? (
        <OtherText grey>
          Today&apos;s menu (Lunch): Jollof Rice & Chicken · Vegetable Pasta — published. Avg cost/portion $
          {costPerPortion(dishes[0]).toFixed(2)} / ${costPerPortion(dishes[1]).toFixed(2)}.
        </OtherText>
      ) : (
        <OtherText grey>
          Orders-only flow: finalized meals generate immutable Contractor Orders. No client stock tracked at this kitchen.
        </OtherText>
      )}
    </Box>
  )
}
